import { createContext, useCallback, useContext, useEffect } from "react";
import { AppEvents } from "../types/events";

type EventContextType = {
    emit: <K extends keyof AppEvents>(type: K, event?: AppEvents[K]) => void;
    on: <K extends keyof AppEvents>(
        type: K,
        handler: (event: AppEvents[K]) => void,
    ) => void;
    off: <K extends keyof AppEvents>(
        type: K,
        handler?: (event: AppEvents[K]) => void,
    ) => void;
};

export const EventsProviderContext = createContext<EventContextType | null>(
    null,
);

export function useEventBus<K extends keyof AppEvents>(
    eventName?: K,
    handler?: (event: AppEvents[K]) => void,
) {
    const context = useContext(EventsProviderContext);

    if (!context) {
        throw new Error("useEventBus must be used within an EventsProvider");
    }

    const { emit, on, off } = context;

    useEffect(() => {
        if (eventName && handler) {
            on(eventName, handler);
            return () => off(eventName, handler);
        }
        return () => {}; // Fonction de nettoyage vide pour satisfaire ESLint
    }, [eventName, handler, on, off]);

    const emitEvent = useCallback(
        <T extends keyof AppEvents>(type: T, event?: AppEvents[T]) => {
            emit(type, event);
        },
        [emit],
    );

    return { emit: emitEvent, on, off };
}
